import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import toastr from 'toastr'
import { track } from '../../../analytics'
import ApiPayments from '../../../api/ApiPayments'
import PaymentErrorBoundary from './PaymentErrorBoundary'

// Inner component that uses Stripe hooks - only renders when Elements is ready
function PaymentForm({
  selectedLeague,
  primaryPlayer,
  selectedSkillLevel,
  nominatedPartner,
  validatedAccessCode,
  stepNumber,
  onNext,
  onBack,
  paymentIntentId,
}) {
  const stripe = useStripe()
  const elements = useElements()

  // Refs for cleanup
  const successTimeoutRef = useRef(null)

  // State management
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [paymentSucceeded, setPaymentSucceeded] = useState(false)

  // Prevent navigation away from page during payment
  useEffect(() => {
    if (!isProcessing) return

    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = '' // Chrome requires returnValue to be set
      return '' // Some browsers use return value
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isProcessing])

  // Cleanup success timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    if (!paymentIntentId) {
      setError('Payment not initialized. Please refresh and try again.')
      return
    }

    setIsProcessing(true)
    setError(null)

    // Store error code for analytics
    let errorCode = null

    try {
      // Track payment attempt (safe to ignore failures)
      try {
        track('payment_attempted', {
          league_id: selectedLeague?.id,
          player_id: primaryPlayer?.id,
          amount: selectedLeague?.entry_fee,
          method: 'stripe',
        })
      } catch (analyticsErr) {
        // Safe to ignore - analytics failures should never block user actions
        console.error('Analytics tracking error:', analyticsErr)
      }

      // Confirm payment with Stripe
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/registration-complete`,
        },
        redirect: 'if_required', // Only redirect if necessary (3D Secure, etc.)
      })

      // Handle Stripe errors
      if (stripeError) {
        // Store error code for analytics
        errorCode = stripeError.code || stripeError.type || 'unknown_error'

        let userMessage = 'Payment failed. Please try again.'

        // Error message constants
        const INVALID_CARD_MESSAGE =
          'The card number is invalid. Please check and try again.'
        const CONNECTION_ERROR_MESSAGE =
          'Connection error. Please check your internet and try again.'

        // Provide user-friendly messages for common Stripe errors
        if (stripeError.type === 'card_error') {
          if (stripeError.code === 'card_declined') {
            userMessage =
              'Your card was declined. Please check your card details or try a different card.'
          } else if (stripeError.code === 'insufficient_funds') {
            userMessage =
              'Your card has insufficient funds. Please try a different card.'
          } else if (stripeError.code === 'expired_card') {
            userMessage = 'Your card has expired. Please use a different card.'
          } else if (stripeError.code === 'incorrect_cvc') {
            userMessage = 'Incorrect security code. Please check and try again.'
          } else if (stripeError.code === 'invalid_number') {
            userMessage = INVALID_CARD_MESSAGE
          } else {
            userMessage = stripeError.message || userMessage
          }
        } else if (stripeError.type === 'validation_error') {
          userMessage = INVALID_CARD_MESSAGE
        } else if (
          stripeError.type === 'api_connection_error' ||
          stripeError.code === 'network_error'
        ) {
          userMessage = CONNECTION_ERROR_MESSAGE
        } else {
          userMessage = stripeError.message || userMessage
        }

        throw new Error(userMessage)
      }

      // Complete registration
      const registrationData = {
        player_id: primaryPlayer.id,
        league_id: selectedLeague.id,
        skill_level: selectedSkillLevel,
        access_code: validatedAccessCode || null,
        ...(nominatedPartner?.type === 'player'
          ? { partner_player_id: nominatedPartner.id }
          : {}),
      }

      const result = await ApiPayments.confirmAndRegister(
        paymentIntentId,
        registrationData,
      )

      if (!result.success) {
        throw new Error('Failed to complete registration after payment')
      }

      // Success!
      setPaymentSucceeded(true)
      toastr.success('Payment processed successfully!')

      // Track payment success (safe to ignore failures)
      try {
        track('payment_succeeded', {
          league_id: selectedLeague?.id,
          player_id: primaryPlayer?.id,
          amount: selectedLeague?.entry_fee,
        })
      } catch (analyticsErr) {
        // Safe to ignore - analytics failures should never block user actions
        console.error('Analytics tracking error:', analyticsErr)
      }

      // Brief delay to show success state before advancing
      // Store timeout ref for cleanup to prevent memory leaks
      successTimeoutRef.current = setTimeout(() => {
        onNext()
      }, 800)
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred'
      setError(errorMessage)
      toastr.error(errorMessage)
      console.error('Payment error:', err)

      // Track payment failure (safe to ignore failures)
      try {
        track('payment_failed', {
          league_id: selectedLeague?.id,
          error_code: errorCode || 'generic_error',
          error_message: errorMessage,
        })
      } catch (analyticsErr) {
        // Safe to ignore - analytics failures should never block user actions
        console.error('Analytics tracking error:', analyticsErr)
      }

      setIsProcessing(false)
    }
  }

  const handleRetry = () => {
    setError(null)
    setIsProcessing(false)
  }

  const handleCancel = () => {
    if (
      isProcessing &&
      !window.confirm(
        'Payment is in progress. Are you sure you want to cancel?',
      )
    ) {
      return
    }

    // Track payment cancellation (safe to ignore failures)
    try {
      track('payment_cancelled', {
        league_id: selectedLeague?.id,
      })
    } catch (analyticsErr) {
      // Safe to ignore - analytics failures should never block user actions
      console.error('Analytics tracking error:', analyticsErr)
    }

    onBack()
  }

  const entryFee = selectedLeague?.entry_fee || 0
  const formattedFee = `$${entryFee.toFixed(2)}`

  return (
    <div className="registration-step">
      <h4 className="step-title">Step {stepNumber}: Payment</h4>
      <p className="step-description">
        Complete your payment to finalize registration for {selectedLeague.name}
        .
      </p>

      <div className="payment-summary">
        <div className="payment-summary-item">
          <span>League Registration Fee</span>
          <span className="payment-amount">{formattedFee}</span>
        </div>
        <div className="payment-summary-total">
          <span>Total</span>
          <span className="payment-amount-total">{formattedFee}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="payment-element-container">
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />
        </div>

        {error && (
          <div
            className="alert alert-danger mt-3"
            role="alert"
            aria-live="assertive"
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Payment Error:</strong> {error}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleRetry}
                disabled={isProcessing}
                aria-label="Retry payment"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {paymentSucceeded && (
          <div className="alert alert-success mt-3" role="alert">
            <strong>Payment successful!</strong> Redirecting...
          </div>
        )}

        <div className="step-navigation">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
            disabled={isProcessing || paymentSucceeded}
            aria-label="Go back to previous step"
          >
            {isProcessing ? 'Cancel' : 'Back'}
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-lg flex-grow-1"
            disabled={isProcessing || !stripe || !elements || paymentSucceeded}
            // Stripe hooks (useStripe/useElements) return null during initialization
            // This check prevents submission before Stripe is fully ready
            aria-label={
              isProcessing
                ? 'Processing payment'
                : `Pay ${formattedFee} and register`
            }
          >
            {isProcessing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                Processing...
              </>
            ) : paymentSucceeded ? (
              'Payment Complete ✓'
            ) : (
              `Pay ${formattedFee}`
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

PaymentForm.propTypes = {
  selectedLeague: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    entry_fee: PropTypes.number.isRequired,
  }).isRequired,
  primaryPlayer: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  selectedSkillLevel: PropTypes.string,
  nominatedPartner: PropTypes.shape({
    type: PropTypes.oneOf(['player', 'email']).isRequired,
    id: PropTypes.number,
    email: PropTypes.string,
    label: PropTypes.string,
  }),
  validatedAccessCode: PropTypes.string,
  stepNumber: PropTypes.number,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  paymentIntentId: PropTypes.string.isRequired,
}

// Outer component that fetches clientSecret and wraps PaymentForm with Elements
function PaymentStep({
  selectedLeague,
  primaryPlayer,
  selectedSkillLevel,
  nominatedPartner,
  _selectedCourt, // Not used in current implementation; reserved for future use
  validatedAccessCode,
  stepNumber = 4, // Default to 4 for standalone use; will be updated during integration in #835
  onNext,
  onBack,
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const [paymentIntentId, setPaymentIntentId] = useState(null)
  const [stripePromise, setStripePromise] = useState(null)
  const [stripeConfigured, setStripeConfigured] = useState(false)
  const [retryCounter, setRetryCounter] = useState(0)

  useEffect(() => {
    try {
      track('registration_step_viewed', {
        step: stepNumber,
        step_name: 'payment',
        league_id: selectedLeague?.id,
        amount: selectedLeague?.entry_fee,
      })
    } catch (analyticsErr) {
      console.error('Analytics tracking error:', analyticsErr)
    }
  }, [selectedLeague?.id, selectedLeague?.entry_fee, stepNumber])

  // Retry fetching payment intent
  const handleRetryIntent = () => {
    setError(null)
    setRetryCounter((prev) => prev + 1)
  }

  // Initialize Stripe on mount
  useEffect(() => {
    const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    if (envKey) {
      setStripeConfigured(true)
      setStripePromise(loadStripe(envKey))
    } else {
      console.warn('[PaymentStep] No Stripe publishable key found')
      setError('Payment system not configured')
      setStripeConfigured(false)
      setIsLoading(false)
    }
  }, [])

  // Fetch clientSecret on mount
  useEffect(() => {
    async function fetchIntent() {
      if (!selectedLeague?.id) {
        setIsLoading(false)
        return
      }

      if (!stripeConfigured) {
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Access codes are validated in confirmAndRegister, not needed here
        const result = await ApiPayments.createIntent(selectedLeague.id)

        // Handle free leagues (no payment required) - skip payment step
        if (result.free) {
          // For free leagues, automatically proceed to next step
          // This is defensive - parent should skip this step for free leagues
          toastr.info('No payment required for this league')
          // Don't update state after calling onNext to avoid updating unmounted component
          onNext()
          return
        }

        setClientSecret(result.clientSecret)
        setPaymentIntentId(result.paymentIntentId)
        setIsLoading(false)
      } catch (err) {
        const errorMessage =
          err.message || 'Failed to initialize payment. Please try again.'
        setError(errorMessage)
        setIsLoading(false)
        console.error('Error fetching payment intent:', err)

        toastr.error(errorMessage)
      }
    }

    fetchIntent()
  }, [
    selectedLeague?.id,
    stripePromise,
    retryCounter,
    onNext,
    stripeConfigured,
  ])

  if (!selectedLeague) {
    return (
      <div className="registration-step">
        <p>Missing league information. Please go back and select a league.</p>
        <button className="btn btn-outline-secondary" onClick={onBack}>
          Back
        </button>
      </div>
    )
  }

  const entryFee = selectedLeague?.entry_fee || 0
  const formattedFee = `$${entryFee.toFixed(2)}`

  // Show loading state while fetching clientSecret
  if (isLoading) {
    return (
      <div className="registration-step">
        <h4 className="step-title">Step {stepNumber}: Payment</h4>
        <p className="step-description">
          Complete your payment to finalize registration for{' '}
          {selectedLeague.name}.
        </p>

        <div className="payment-summary">
          <div className="payment-summary-item">
            <span>League Registration Fee</span>
            <span className="payment-amount">{formattedFee}</span>
          </div>
          <div className="payment-summary-total">
            <span>Total</span>
            <span className="payment-amount-total">{formattedFee}</span>
          </div>
        </div>

        <div className="payment-loading">
          <div className="spinner-border text-primary mb-2" role="status">
            <span className="visually-hidden">
              Initializing payment form...
            </span>
          </div>
          <p>Initializing payment form...</p>
        </div>
      </div>
    )
  }

  // Show error state if clientSecret fetch failed
  if (!clientSecret && error) {
    return (
      <div className="registration-step">
        <h4 className="step-title">Step {stepNumber}: Payment</h4>
        <p className="step-description">
          Complete your payment to finalize registration for{' '}
          {selectedLeague.name}.
        </p>

        <div className="payment-summary">
          <div className="payment-summary-item">
            <span>League Registration Fee</span>
            <span className="payment-amount">{formattedFee}</span>
          </div>
          <div className="payment-summary-total">
            <span>Total</span>
            <span className="payment-amount-total">{formattedFee}</span>
          </div>
        </div>

        <div className="payment-error-state">
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Initialization Error:</strong> {error}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleRetryIntent}
                disabled={isLoading}
                aria-label="Retry initializing payment"
              >
                Retry
              </button>
            </div>
          </div>
          <div className="step-navigation">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onBack}
              aria-label="Go back to previous step"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render payment form wrapped in Elements with clientSecret
  // Only render once both stripePromise and clientSecret are ready
  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
    },
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm
        selectedLeague={selectedLeague}
        primaryPlayer={primaryPlayer}
        selectedSkillLevel={selectedSkillLevel}
        nominatedPartner={nominatedPartner}
        validatedAccessCode={validatedAccessCode}
        stepNumber={stepNumber}
        onNext={onNext}
        onBack={onBack}
        paymentIntentId={paymentIntentId}
      />
    </Elements>
  )
}

PaymentStep.propTypes = {
  selectedLeague: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    entry_fee: PropTypes.number.isRequired,
  }).isRequired,
  primaryPlayer: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  selectedSkillLevel: PropTypes.string,
  nominatedPartner: PropTypes.shape({
    type: PropTypes.oneOf(['player', 'email']).isRequired,
    id: PropTypes.number,
    email: PropTypes.string,
    label: PropTypes.string,
  }),
  _selectedCourt: PropTypes.any, // Reserved for future use
  validatedAccessCode: PropTypes.string,
  stepNumber: PropTypes.number,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
}

// Export PaymentStep wrapped with Error Boundary
function PaymentStepWithErrorBoundary(props) {
  return (
    <PaymentErrorBoundary onBack={props.onBack}>
      <PaymentStep {...props} />
    </PaymentErrorBoundary>
  )
}

PaymentStepWithErrorBoundary.propTypes = PaymentStep.propTypes

export default PaymentStepWithErrorBoundary
